// apps/client/src/pages/SharedCompanyOverviewPage.tsx
// PUBLIC SHARED COMPANY OVERVIEW - VALUTTEN-branded read-only company snapshot.
// Mirrors the succession-readiness share page pattern (snapshot-at-share-time,
// HMAC-validated via callable).

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Calendar,
  Vault,
  Activity,
  Handshake,
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
} from 'lucide-react';
import { formatCompactCurrency } from '../utils/formatters';

interface LenderBreakdownEntry {
  lender: string;
  loanCount: number;
  loanBookCents: number;
  percentage: number;
}

interface BrokerBreakdownEntry {
  brokerId: string;
  brokerName: string | null;
  loanCount: number;
  /** Trail earned inside the reported period. Absent on legacy share documents. */
  periodTrailCents?: number;
  /** Legacy rolling-12-month trail. Absent on documents minted after the period rework. */
  annualTrailCents?: number;
  percentage: number;
}

interface MonthlySettlementEntry {
  month: string;
  loanCount: number;
  totalLoanAmountCents: number;
  /**
   * 'pending_upfront' marks a month the tenant HAS reported (it carries trail
   * dollars) but whose upfront commission rows have not arrived yet. A
   * settlement is only recognised when its upfront row lands, so the month is
   * genuinely empty on a settlement basis rather than missing from the data.
   */
  status?: 'settled' | 'pending_upfront';
}

/** Line of Trail movement across the reported period. */
interface LotGrowth {
  fromMonth: string;
  from: number;
  toMonth: string;
  to: number;
  growthPercent: number | null;
}

// --- Group Snapshot additions. All optional: share documents minted before the
// config existed carry none of these, and the page must render unchanged for them.
interface OfficeBreakdownEntry {
  officeId: string;
  officeName: string | null;
  trailLoanCount: number;
  loanBookCents: number | null;
  periodTrailCents: number;
  percentageOfBook: number | null;
}

interface LoanBookEntry {
  loanBookId: string;
  name: string;
  entityCode: string | null;
  officeName: string | null;
  isAcquired: boolean;
  trailLoanCount: number;
  periodTrailCents: number;
  reportingStatus?: 'reported' | 'awaiting' | 'not_reporting';
  typicalMonthlyTrailCents?: number | null;
}

interface RunCompletenessSummary {
  months: string[];
  status: 'complete' | 'incomplete' | 'incomplete_material';
  perTemplate: Array<{ templateId: string; month: string; runsPresent: number[]; missingRuns: number[] }>;
  missingTemplates: string[];
  latestRunDate: string | null;
  deltaPercent: number | null;
}

interface CompanyOverviewData {
  totalLoanBookCents: number;
  activeLoanCount: number;
  // --- LEGACY-ONLY fields. Documents minted before the Group Snapshot period
  // rework carry these; documents minted after it carry none of them. Every
  // read below MUST be guarded or a new document crashes the page.
  totalLoansEver?: number;
  annualTrailIncomeCents?: number;
  loansNowVs12MonthsAgo?: {
    current: number;
    previous: number;
    growthPercent: number | null;
  };
  settlementsLast12Months?: {
    totalLoanCount: number;
    totalLoanAmountCents: number;
    monthly: MonthlySettlementEntry[];
  };
  topLenders: LenderBreakdownEntry[];
  topBrokers: BrokerBreakdownEntry[];
  generatedAt: string;
  // Group Snapshot additions (absent on legacy share documents)
  periodLabel?: string;
  periodTrailCents?: number;
  runCompleteness?: RunCompletenessSummary;
  officeBreakdown?: OfficeBreakdownEntry[];
  unattributedBookCents?: number;
  periodSettlementCents?: number;
  periodSettlementCount?: number;
  loanBooks?: LoanBookEntry[];
  awaitingBooks?: { count: number; typicalMonthlyTrailCents: number };
  /** The months the period actually resolved to. Bounds every month-grained render. */
  resolvedMonths?: string[];
  /** Settlement-date-basis monthly series, already bounded to `resolvedMonths` server-side. */
  settlementSeries?: MonthlySettlementEntry[];
  /** Distinct loans paid trail in `lotAsOfMonth`. Supersedes the mislabelled active-loan count. */
  periodEndLineOfTrail?: number;
  lotAsOfMonth?: string;
  lotGrowth?: LotGrowth;
  /**
   * Sum of the per-office loans-on-trail column. Published so the reader gets a
   * reconciling statement: a loan serviced by two offices counts in both, so the
   * office rows exceed the group figure by design.
   */
  officeLineOfTrailSum?: number;
  latestRunDate?: string | null;
}

interface ShareData {
  shareId: string;
  companyName: string;
  createdAt: string;
  data: CompanyOverviewData;
}

// The app-wide three-significant-figure compact rule, so the public page reads
// the same as the in-app overview it was shared from.
const formatCents = (cents: number): string => formatCompactCurrency(cents);

const formatCount = (n: number): string => n.toLocaleString('en-AU');

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * 'YYYY-MM' or 'YYYY-MM-DD' to 'Mon YYYY'. Built from the string rather than
 * `new Date(...)` because a bare 'YYYY-MM-01' parses as UTC midnight and drops
 * to the previous month for a reader in Brisbane.
 */
const formatMonthLabel = (month: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(month);
  if (!match) return month;
  const index = Number(match[2]) - 1;
  if (index < 0 || index > 11) return month;
  return `${MONTH_LABELS[index]} ${match[1]}`;
};

/**
 * Stat card matching the client app's StatCardWithGrowth.
 *
 * The shared page renders outside AppLayout and cannot import the component, so
 * its treatment is mirrored here verbatim: the 135deg dark base, a colour
 * overlay at 60%, the h-1 top accent that fades in at 15% and out at 85%, the
 * text-4xl value, and the p-3 rounded-xl icon tile with a colour glow. Anything
 * else would look like a different product to the person reading it.
 */
const StatCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  color: string;
  icon: React.ReactNode;
  growthPercent?: number | null;
  growthLabel?: string;
}> = ({ title, value, subtitle, color, icon, growthPercent, growthLabel }) => {
  const growthColor = growthPercent == null ? 'text-gray-400'
    : growthPercent > 0 ? 'text-green-400' : growthPercent < 0 ? 'text-red-400' : 'text-gray-400';
  const growthIcon = growthPercent == null ? '→' : growthPercent > 0 ? '↑' : growthPercent < 0 ? '↓' : '→';
  return (
    <div className="relative rounded-lg shadow-xl overflow-hidden break-avoid">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)' }}
      />
      <div
        className="absolute inset-0 opacity-60"
        style={{ background: `linear-gradient(135deg, ${color}26 0%, ${color}0D 50%, rgba(17,24,39,0.8) 100%)` }}
      />
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, transparent 0%, ${color} 15%, ${color} 85%, transparent 100%)` }}
      />
      {/* Fixed rows so the values sit on a common baseline across all four
          cards. Titles wrapping to two lines was pushing each value to a
          different height, which read as jagged. Titles are kept short enough
          not to wrap; `truncate` is the backstop, and the detail lives in the
          subtitle where a second line does no harm. */}
      <div className="relative p-6 z-10 flex flex-col h-full">
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-gray-400 text-sm font-medium uppercase tracking-wider truncate">{title}</p>
            <p className="text-4xl font-bold text-white mt-2 leading-none">{value}</p>
            {growthPercent != null && (
              <div className="mt-2 flex items-center gap-1.5">
                <span className={`text-lg font-semibold ${growthColor}`}>{growthIcon}</span>
                <span className={`text-sm font-medium ${growthColor}`}>
                  {Math.abs(growthPercent).toFixed(1)}%
                </span>
                <span className="text-xs text-gray-500">{growthLabel ?? 'vs last year'}</span>
              </div>
            )}
          </div>
          <div
            className="p-3 rounded-xl flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`, boxShadow: `0 0 20px ${color}20`, color }}
          >
            {icon}
          </div>
        </div>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">{subtitle}</p>
        )}
      </div>
    </div>
  );
};

const SharedCompanyOverviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const validateFunc = httpsCallable<{ token: string }, ShareData>(
          functions,
          'validateCompanyShareToken',
        );
        const response = await validateFunc({ token });
        setShareData(response.data);
      } catch (err: any) {
        // Surface read-failure to console per memory rule (no silent fail)
        console.error('Failed to validate company share token:', err);
        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Shared company overview not found');
        } else {
          setError(err.message || 'Failed to load shared overview');
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading company overview…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Overview</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to VALUTTEN
          </Link>
        </div>
      </div>
    );
  }

  if (!shareData) return null;

  const { data } = shareData;
  const createdDate = new Date(shareData.createdAt);
  const formattedDate = createdDate.toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // GUARDED READS. `loansNowVs12MonthsAgo` and `settlementsLast12Months` are
  // legacy-only fields: a document minted after the period rework carries
  // neither, and an unguarded dereference here took the whole page down.
  const legacyGrowth = data.loansNowVs12MonthsAgo ?? null;
  const legacyMonthly = data.settlementsLast12Months?.monthly ?? [];

  // The growth block prefers the period Line of Trail movement and falls back to
  // the legacy rolling-12-month comparison only for documents that carry it.
  const lotGrowth = data.lotGrowth ?? null;
  const shownGrowthPercent = lotGrowth ? lotGrowth.growthPercent : legacyGrowth?.growthPercent ?? null;
  const growthColor =
    shownGrowthPercent === null
      ? 'text-slate-400'
      : shownGrowthPercent >= 0
      ? 'text-emerald-400'
      : 'text-red-400';
  const GrowthIcon =
    shownGrowthPercent === null ? BarChart3 : shownGrowthPercent >= 0 ? TrendingUp : TrendingDown;

  // Monthly settlement chart. `settlementSeries` is the settlement-date-basis
  // series and is already bounded server-side; a legacy document's rolling
  // twelve months is bounded here to the resolved period so a month outside the
  // reported window (the October 2026 Concierge line, for one) cannot render.
  const resolvedMonths = data.resolvedMonths;
  const settlementRowsRaw: MonthlySettlementEntry[] = data.settlementSeries ?? legacyMonthly;
  const settlementRows =
    resolvedMonths && resolvedMonths.length > 0
      ? settlementRowsRaw.filter((r) => resolvedMonths.includes(r.month))
      : settlementRowsRaw;
  const maxMonthlyCents = settlementRows.reduce(
    (m, r) => Math.max(m, r.totalLoanAmountCents),
    0,
  );

  // Line of Trail card. Without `periodEndLineOfTrail` the only count available
  // is `activeLoanCount`, which is NOT a line of trail, so the card is retitled
  // rather than left mislabelled on an already-issued link.
  const hasPeriodLot = typeof data.periodEndLineOfTrail === 'number';
  const lotAsAtLabel = data.lotAsOfMonth ? formatMonthLabel(data.lotAsOfMonth) : null;

  // Top Brokers moved from a rolling annual trail to the reported period.
  const brokersOnPeriodTrail = data.topBrokers.some(
    (b) => typeof b.periodTrailCents === 'number',
  );

  return (
    <div className="min-h-screen bg-slate-900">
      {/* PRINT SUPPORT (Group Snapshot). Deliberate divergence from the other
          Shared* report pages, which BLOCK printing via
          `@media print { body { display: none } }` because they are view-only by
          product decision. This report exists to be handed to an external reader,
          so the browser's own print-to-PDF is the delivery route. NOTE: VALUTTEN
          still generates and serves NO file - the no-data-export invariant holds;
          the viewer's browser produces the PDF locally. Do not copy this block
          into the other shared pages, and do not remove their blockers. */}
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          .break-avoid { break-inside: avoid; page-break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header — the client app's PageHeader treatment, not a coloured banner.
            Same radial brand-blue glow, same title scale and tracking, same
            fading gradient rule, so a recipient who later sees the app
            recognises it as the same product. The logo sits above the title
            because this page is read by someone outside the business who needs
            to know what produced the figures. */}
        <div className="pt-2 pb-4 relative overflow-hidden mb-6">
          <div
            className="absolute top-[-2.5rem] left-[0.5rem] w-[14rem] h-[9rem] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse, rgba(37, 99, 235, 0.15) 0%, transparent 70%)' }}
          />
          <div className="relative z-10">
            <img
              src="/valutten-logo.jpg"
              alt="VALUTTEN"
              className="h-9 md:h-10 mb-5"
              style={{ mixBlendMode: 'lighten' }}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                {shareData.companyName}
              </h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[0.625rem] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-400">
                Group Snapshot
              </span>
            </div>
            <p className="text-gray-400 text-sm mt-1 leading-relaxed">
              {data.periodLabel ? `${data.periodLabel} · ` : ''}Read-only shared snapshot · Confidential
            </p>
          </div>
          <div
            className="relative z-10 mt-5 h-px"
            style={{ background: 'linear-gradient(90deg, rgba(75, 85, 99, 0.3), transparent 60%)' }}
          />
        </div>

        {/* Metadata */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Snapshot Generated</p>
              <p className="text-white font-medium">{formattedDate}</p>
            </div>
          </div>
        </div>

        {/* PROVISIONAL BANNER. Rendered from the FROZEN completeness object on the
            share document, so a later client change cannot drop it. ~90% of a
            month's trail settles in Run 3; a period missing it understates. */}
        {data.runCompleteness && data.runCompleteness.status !== 'complete' && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-5 mb-6 flex gap-3 items-start break-avoid">
            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white mb-1">Provisional figures</p>
              <p className="text-sm text-amber-200/90 leading-relaxed">
                Not all commission runs have been received for this period. Around 90% of a
                month&rsquo;s trail settles in the third run, so the figures below understate the
                period and will rise once the outstanding runs land.
                {data.runCompleteness.missingTemplates.length > 0 && (
                  <> {data.runCompleteness.missingTemplates.length} reporting source(s) have not
                  reported at all for this period.</>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Topline Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 items-stretch">
          <StatCard
            title="Loan Book"
            value={formatCents(data.totalLoanBookCents)}
            subtitle={`across ${formatCount(data.activeLoanCount)} active loans`}
            color="#3b82f6"
            icon={<Vault className="w-6 h-6" />}
          />
          <StatCard
            title="Trail"
            value={formatCents(
              typeof data.periodTrailCents === 'number'
                ? data.periodTrailCents
                : data.annualTrailIncomeCents ?? 0,
            )}
            subtitle={typeof data.periodTrailCents === 'number'
              ? `${data.periodLabel ?? ''} · net of deductions · ex GST`.replace(/^ · /, '')
              : 'trailing 12 months · net of deductions'}
            color="#10b981"
            icon={<TrendingUp className="w-6 h-6" />}
          />
          {/* Line of Trail is the count of DISTINCT loans that paid trail in the
              month shown. A legacy document has no such figure, only
              `activeLoanCount`, which counts loans active in the book; showing
              that under a "Line of Trail" title mislabelled it, so the card is
              retitled instead. */}
          <StatCard
            title={hasPeriodLot ? 'Line of Trail' : 'Active loans'}
            value={formatCount(
              hasPeriodLot ? (data.periodEndLineOfTrail as number) : data.activeLoanCount,
            )}
            subtitle={
              hasPeriodLot
                ? lotAsAtLabel
                  ? `loans on trail as at ${lotAsAtLabel}`
                  : 'loans on trail'
                : 'active loans in the group book'
            }
            color="#a855f7"
            icon={<Activity className="w-6 h-6" />}
          />
          {/* Settled MUST follow the configured period. `settlementsLast12Months`
              is the legacy field: a rolling twelve months from the MINT DATE,
              which is not the reported period. Labelling a card with one window
              and filling it from another shows the reader a figure that does not
              belong to the period they are reading. Measured on the
              Sep 2025 - Aug 2026 link: the legacy field gave $538,320,200 where
              the period is $521,696,545 (the UNIQUE figure - a loan settles once
              but appears under several month/office/template rows, so a summed
              figure overcounts at $636,740,034). The legacy field is used only
              as a fallback for share docs minted before the config existed,
              which carry no period figure. */}
          <StatCard
            title="Settled"
            value={formatCents(
              typeof data.periodSettlementCents === 'number'
                ? data.periodSettlementCents
                : data.settlementsLast12Months?.totalLoanAmountCents ?? 0,
            )}
            subtitle={typeof data.periodSettlementCents === 'number'
              ? `${data.periodLabel ?? 'this period'} · across ${formatCount(data.periodSettlementCount ?? 0)} loans`
              : `last 12 months · across ${formatCount(data.settlementsLast12Months?.totalLoanCount ?? 0)} loans`}
            color="#f59e0b"
            icon={<Handshake className="w-6 h-6" />}
          />
        </div>

        {/* Book Growth. `lotGrowth` measures the Line of Trail across the
            reported period and is the figure to show when present. A null
            percent is stated as unavailable rather than rendered as zero or
            fabricated as 100%: an incomplete set of runs cannot support a growth
            claim. Legacy documents keep their rolling twelve-month block. */}
        {lotGrowth ? (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-8 break-avoid">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <GrowthIcon className={`w-4 h-4 ${growthColor}`} /> Book Growth
            </h3>
            <div className="flex items-baseline gap-4 flex-wrap">
              {lotGrowth.growthPercent === null ? (
                <span className="text-sm text-slate-400">
                  Growth not stated (period runs incomplete)
                </span>
              ) : (
                <span className={`text-3xl font-bold ${growthColor}`}>
                  {lotGrowth.growthPercent > 0 ? '+' : ''}
                  {lotGrowth.growthPercent.toFixed(1)}%
                </span>
              )}
              <span className="text-sm text-slate-400">
                {formatCount(lotGrowth.from)} to {formatCount(lotGrowth.to)} loans on trail,{' '}
                {formatMonthLabel(lotGrowth.fromMonth)} to {formatMonthLabel(lotGrowth.toMonth)}
              </span>
            </div>
          </div>
        ) : legacyGrowth && legacyGrowth.growthPercent !== null ? (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <GrowthIcon className={`w-4 h-4 ${growthColor}`} /> Book Growth (12 months)
            </h3>
            <div className="flex items-baseline gap-4">
              <span className={`text-3xl font-bold ${growthColor}`}>
                {legacyGrowth.growthPercent > 0 ? '+' : ''}
                {legacyGrowth.growthPercent}%
              </span>
              <span className="text-sm text-slate-400">
                {formatCount(legacyGrowth.previous)} to {formatCount(legacyGrowth.current)} loans on
                trail
              </span>
            </div>
          </div>
        ) : null}

        {/* Monthly Settlement Volume */}
        {settlementRows.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-amber-400" /> Monthly Settlement Volume
            </h3>
            <p className="text-xs text-slate-500 mb-4 mt-1">
              {data.periodLabel ? `${data.periodLabel}, by settlement date` : 'By settlement date'}
            </p>
            <div className="space-y-2">
              {settlementRows.map((m) => {
                const pending = m.status === 'pending_upfront';
                return (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="text-xs font-mono text-slate-400 w-20 shrink-0">
                      {formatMonthLabel(m.month)}
                    </span>
                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                      {!pending && (
                        <div
                          className="h-full bg-amber-500 rounded-full"
                          style={{
                            width: `${maxMonthlyCents > 0
                              ? Math.min((m.totalLoanAmountCents / maxMonthlyCents) * 100, 100)
                              : 0
                              }%`,
                          }}
                        />
                      )}
                    </div>
                    <span
                      className={`text-xs w-44 text-right shrink-0 ${pending ? 'text-amber-300/90' : 'text-slate-400'}`}
                    >
                      {pending
                        ? 'upfronts not yet received'
                        : `${formatCents(m.totalLoanAmountCents)} (${m.loanCount})`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Book & trail by office (Group Snapshot). Office rows come from the same
            statement as the group total, so they reconcile by construction. */}
        {data.officeBreakdown && data.officeBreakdown.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6 break-avoid">
            <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Book &amp; Trail by Office
              {data.periodLabel && (
                <span className="text-xs font-normal text-slate-500">{data.periodLabel}</span>
              )}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left font-semibold py-2 pr-4">Office</th>
                    <th className="text-right font-semibold py-2 px-4">Loans on trail</th>
                    <th className="text-right font-semibold py-2 px-4">Loan book</th>
                    <th className="text-right font-semibold py-2 pl-4">Trail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.officeBreakdown.map((o) => (
                    <tr key={o.officeId} className="border-t border-slate-700/50">
                      <td className="py-2.5 pr-4 text-white font-medium">
                        {o.officeName ?? (
                          <span className="italic text-slate-500">Unattributed</span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right text-slate-300">
                        {formatCount(o.trailLoanCount)}
                      </td>
                      <td className="py-2.5 px-4 text-right text-white">
                        {o.loanBookCents === null ? '—' : formatCents(o.loanBookCents)}
                        {o.percentageOfBook !== null && (
                          <span className="text-xs text-slate-500 ml-1.5">({o.percentageOfBook}%)</span>
                        )}
                      </td>
                      <td className="py-2.5 pl-4 text-right text-emerald-400">
                        {formatCents(o.periodTrailCents)}
                      </td>
                    </tr>
                  ))}
                  {typeof data.periodTrailCents === 'number' && (
                    <tr className="border-t-2 border-slate-600 font-semibold">
                      <td className="py-2.5 pr-4 text-white">Group total</td>
                      <td className="py-2.5 px-4" />
                      <td className="py-2.5 px-4 text-right text-white">
                        {formatCents(data.totalLoanBookCents)}
                      </td>
                      <td className="py-2.5 pl-4 text-right text-emerald-400">
                        {formatCents(data.periodTrailCents)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Loan book is attributed to the office that owns the loan on a net commission basis;
              trail is attributed to the office that received the payment. The two can differ for
              loans written across offices.
              {typeof data.unattributedBookCents === 'number' && data.unattributedBookCents > 0 && (
                <> {formatCents(data.unattributedBookCents)} of loan book is not attributable to a
                single office and is included in the group total only.</>
              )}
            </p>
            {/* Loans on trail do not add across offices, and forcing them to
                would be wrong. State the reconciliation instead so the reader
                does not read the difference as an error. */}
            {typeof data.officeLineOfTrailSum === 'number' && hasPeriodLot && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Loans on trail are counted per office
                {lotAsAtLabel ? ` as at ${lotAsAtLabel}` : ''}; a loan serviced by two offices is
                counted in both, so office rows ({formatCount(data.officeLineOfTrailSum)}) exceed
                the group figure ({formatCount(data.periodEndLineOfTrail as number)}).
              </p>
            )}
          </div>
        )}

        {/* All loan books. Acquired books are flagged, never excluded. */}
        {data.loanBooks && data.loanBooks.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6 break-avoid">
            <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
              <Vault className="w-4 h-4 text-blue-400" /> All Loan Books
              <span className="text-xs font-normal text-slate-500">
                {data.loanBooks.length} books
              </span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left font-semibold py-2 pr-4">Loan book</th>
                    <th className="text-left font-semibold py-2 px-4">Office</th>
                    <th className="text-right font-semibold py-2 px-4">Loans on trail</th>
                    <th className="text-right font-semibold py-2 pl-4">Trail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.loanBooks.map((b) => (
                    <tr key={b.loanBookId} className="border-t border-slate-700/50">
                      <td className="py-2.5 pr-4 text-white font-medium">
                        {b.name}
                        {b.isAcquired && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[0.6rem] font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Acquired
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-slate-400">{b.officeName ?? '—'}</td>
                      <td className="py-2.5 px-4 text-right text-slate-300">
                        {b.trailLoanCount > 0 ? formatCount(b.trailLoanCount) : '—'}
                      </td>
                      <td className="py-2.5 pl-4 text-right">
                        {b.periodTrailCents > 0 ? (
                          <span className="text-emerald-400">{formatCents(b.periodTrailCents)}</span>
                        ) : b.reportingStatus === 'awaiting' ? (
                          <span className="text-amber-400" title="Reports on a lag; not yet received for this period">
                            Awaiting
                            {typeof b.typicalMonthlyTrailCents === 'number' && b.typicalMonthlyTrailCents > 0 && (
                              <span className="block text-xs text-slate-500 font-normal">
                                usually ~{formatCents(b.typicalMonthlyTrailCents)}
                              </span>
                            )}
                          </span>
                        ) : b.reportingStatus === 'reported' ? (
                          // Reported and genuinely nil. Shown as $0, not a dash,
                          // so a measured zero is not read as missing data.
                          <span className="text-slate-400">$0</span>
                        ) : (
                          <span className="text-slate-600">&mdash;</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.awaitingBooks && data.awaitingBooks.count > 0 ? (
              <p className="text-xs text-amber-300/90 mt-3 leading-relaxed">
                {data.awaitingBooks.count} book{data.awaitingBooks.count === 1 ? '' : 's'} that
                normally report{data.awaitingBooks.count === 1 ? 's' : ''} had not yet been received
                for this period, typically about{' '}
                {formatCents(data.awaitingBooks.typicalMonthlyTrailCents)} a month between them.
                Aggregators report a month days to weeks after it closes, so the totals above are
                expected to rise. Books marked &mdash; have not reported for some time.
              </p>
            ) : (
              <p className="text-xs text-slate-500 mt-3">
                $0 means the book reported and paid no trail; &mdash; means it has not reported for
                some time. Both remain part of the group.
              </p>
            )}
          </div>
        )}

        {/* Top Lenders */}
        {data.topLenders.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Top Lenders (by loan book)
            </h3>
            <p className="text-xs text-slate-500 mb-4 mt-1">% of group loan book</p>
            <div className="space-y-2">
              {data.topLenders.map((l) => (
                <div key={l.lender} className="flex items-center gap-3">
                  <span className="text-sm text-slate-300 w-40 truncate shrink-0">
                    {l.lender}
                  </span>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${Math.min(l.percentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400 w-40 text-right shrink-0">
                    {formatCents(l.loanBookCents)} · {l.loanCount} ({l.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Brokers */}
        {data.topBrokers.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-8">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />{' '}
              {brokersOnPeriodTrail
                ? `Top Brokers (by trail, ${data.periodLabel ?? 'this period'})`
                : 'Top Brokers (by annual trail)'}
            </h3>
            <p className="text-xs text-slate-500 mb-4 mt-1">
              {brokersOnPeriodTrail ? '% of period trail' : '% of annual trail'}
            </p>
            <div className="space-y-2">
              {data.topBrokers.map((b) => (
                <div key={b.brokerId} className="flex items-center gap-3">
                  <span className="text-sm text-slate-300 w-40 truncate shrink-0">
                    {b.brokerName || b.brokerId}
                  </span>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${Math.min(b.percentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400 w-40 text-right shrink-0">
                    {formatCents(b.periodTrailCents ?? b.annualTrailCents ?? 0)} · {b.loanCount} (
                    {b.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BASIS OF PREPARATION. Three of the figures above are counted on
            definitions a reader cannot infer from the label, and two of them
            (Settled, Line of Trail) moved when the report was put on a period
            basis. Stating the definitions is what makes the numbers usable in
            diligence; without it a reader reconciling against their own
            spreadsheet reads a definitional difference as an error. */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6 break-avoid">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Basis of preparation</h3>
          <dl className="space-y-3 text-xs text-slate-400 leading-relaxed">
            <div>
              <dt className="text-slate-300 font-semibold">Settled</dt>
              <dd>
                Loans are counted by settlement date and counted once, however many commission rows
                they generate. A settlement is recognised when its upfront commission is received,
                so a month the lender has reported but not yet paid upfronts on shows no settlement
                volume and is marked as such.
              </dd>
            </div>
            <div>
              <dt className="text-slate-300 font-semibold">Line of Trail</dt>
              <dd>
                The number of distinct loans that were paid trail commission in the month shown. It
                is not the number of loans in the book, and it does not add across offices.
              </dd>
            </div>
            <div>
              <dt className="text-slate-300 font-semibold">Office loans on trail</dt>
              <dd>
                Active loans attributed to the office that services them today, as at the month
                shown, rather than to the office that originally wrote them.
              </dd>
            </div>
          </dl>
          {data.latestRunDate && (
            <p className="text-xs text-slate-500 mt-4">
              Most recent commission run reflected in these figures: {data.latestRunDate}.
            </p>
          )}
        </div>

        {/* About VALUTTEN.
            ALWAYS RENDERED. This is VALUTTEN's own attribution and contact block
            on a VALUTTEN-hosted link, so it is not a tenant option - the config
            forces `sections.aboutValutten` true server-side and the settings UI
            offers no toggle for it. The recipient of a snapshot is usually a
            buyer or investor who has never heard of the platform; this is how
            they know what produced the figures and who to ask about them. */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden mb-6 break-avoid">
          <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700" />
          <div className="p-6 md:p-8">
            <img
              src="/valutten-logo.jpg"
              alt="VALUTTEN"
              className="h-9 mb-5"
              style={{ mixBlendMode: 'lighten' }}
            />
            <h3 className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">
              About VALUTTEN
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-3">
              VALUTTEN is an Australian commission intelligence platform built for mortgage broking
              groups. It ingests lender commission files directly, reconciles them at loan level,
              and turns them into a verified view of a group&rsquo;s trail book, loan book,
              settlement volume and broker performance. Every figure in this snapshot comes from
              {' '}{shareData.companyName}&rsquo;s own lender data rather than a manually maintained
              spreadsheet, which is what makes it suitable for diligence.
            </p>
            <p className="text-sm text-slate-400 leading-relaxed">
              The platform is used by broking groups across Australia, built to enterprise
              standards, with all data held in Australia and access controlled at group, office and
              broker level.
            </p>

            <div className="flex flex-wrap gap-6 items-end justify-between mt-6 pt-5 border-t border-slate-700/50">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">
                  To discuss this snapshot
                </p>
                <p className="text-sm text-white font-semibold">Dieter Roylance &middot; Founder</p>
                <a
                  href="mailto:dieter@valutten.com"
                  className="text-sm text-blue-400 hover:text-blue-300 font-semibold"
                >
                  dieter@valutten.com
                </a>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">
                  Platform
                </p>
                <a
                  href="https://valutten.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 font-semibold"
                >
                  valutten.com
                </a>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed mt-5 pt-4 border-t border-slate-700/50">
              Confidential &mdash; a shared, read-only snapshot of {shareData.companyName}, prepared
              at the request of its management. Provided for information only; not financial product
              advice, an offer, or a valuation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedCompanyOverviewPage;
